<template>
  <div class="page-content">
    <div class="user-management">
      <el-card class="user-mgmt-toolbar-card" shadow="never" :body-style="{ padding: '0' }">
      <div class="page-header">
        <div class="search-section">
          <div class="mobile-search-row">
            <div class="search-container">
            <input 
              v-model="searchKeyword" 
              type="text" 
              placeholder="搜索用户名..." 
              class="search-input"
              @input="handleSearch"
            />
            <div class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            </div>
            <button
              type="button"
              class="mobile-filter-toggle"
              @click="mobileFiltersExpanded = !mobileFiltersExpanded"
            >
              筛选
              <span v-if="activeFilterCount" class="mobile-filter-badge">{{ activeFilterCount }}</span>
              <span class="mobile-filter-toggle-icon">{{ mobileFiltersExpanded ? '▲' : '▼' }}</span>
            </button>
          </div>
          <div
            class="filter-container"
            :class="{ 'is-collapsed': isMobileToolbar && !mobileFiltersExpanded }"
          >
            <div class="custom-select" :class="{ disabled: userListLoading }">
              <div class="select-trigger" @click="toggleGameFilterDropdown" :class="{ active: showGameFilterDropdown }">
                <span class="select-value">{{ selectedGameFilter || '选择游戏' }}</span>
                <i class="select-arrow" :class="{ rotated: showGameFilterDropdown }">▼</i>
              </div>
              <div v-if="showGameFilterDropdown" class="select-options">
                <div
                  v-for="game in gameList"
                  :key="game.id"
                  class="select-option"
                  :class="{ selected: selectedGameFilter === game.gameName }"
                  @click="selectGameFilter(game.gameName)"
                >
                  <span class="option-text">{{ game.gameName }}</span>
                </div>
              </div>
            </div>
            <div class="custom-select" :class="{ disabled: userListLoading }">
              <div class="select-trigger" @click="toggleRoleFilterDropdown" :class="{ active: showRoleFilterDropdown }">
                <span class="select-value">{{ selectedRoleFilter || '选择角色' }}</span>
                <i class="select-arrow" :class="{ rotated: showRoleFilterDropdown }">▼</i>
              </div>
              <div v-if="showRoleFilterDropdown" class="select-options">
                <div class="select-option" :class="{ selected: !selectedRoleFilter }" @click="selectRoleFilter('')">
                  <span class="option-text">全部角色</span>
                </div>
                <div class="select-option" :class="{ selected: selectedRoleFilter === '普通用户' }" @click="selectRoleFilter('普通用户')">
                  <span class="option-text">普通用户</span>
                </div>
                <div class="select-option" :class="{ selected: selectedRoleFilter === '超级管理员' }" @click="selectRoleFilter('超级管理员')">
                  <span class="option-text">超级管理员</span>
                </div>
                <div class="select-option" :class="{ selected: selectedRoleFilter === '代理' }" @click="selectRoleFilter('代理')">
                  <span class="option-text">代理</span>
                </div>
                <div class="select-option" :class="{ selected: selectedRoleFilter === '配置管理员' }" @click="selectRoleFilter('配置管理员')">
                  <span class="option-text">配置管理员</span>
                </div>
              </div>
            </div>
            <div class="custom-select" :class="{ disabled: userListLoading }">
              <div class="select-trigger" @click="toggleStatusFilterDropdown" :class="{ active: showStatusFilterDropdown }">
                <span class="select-value">{{ selectedStatusFilter || '选择状态' }}</span>
                <i class="select-arrow" :class="{ rotated: showStatusFilterDropdown }">▼</i>
              </div>
              <div v-if="showStatusFilterDropdown" class="select-options">
                <div class="select-option" :class="{ selected: !selectedStatusFilter }" @click="selectStatusFilter('')">
                  <span class="option-text">全部状态</span>
                </div>
                <div class="select-option" :class="{ selected: selectedStatusFilter === '正常' }" @click="selectStatusFilter('正常')">
                  <span class="option-text">正常</span>
                </div>
                <div class="select-option" :class="{ selected: selectedStatusFilter === '已删除' }" @click="selectStatusFilter('已删除')">
                  <span class="option-text">已删除</span>
                </div>
              </div>
            </div>
            <div v-if="userInfo.is_admin === 1" class="custom-select parent-agent-filter-select" :class="{ disabled: userListLoading, 'is-open': showParentAgentFilterDropdown }">
              <div class="select-trigger" @click="toggleParentAgentFilterDropdown" :class="{ active: showParentAgentFilterDropdown }">
                <span class="select-value">{{ parentAgentFilterDisplayText }}</span>
                <i class="select-arrow" :class="{ rotated: showParentAgentFilterDropdown }">▼</i>
              </div>
              <div v-if="showParentAgentFilterDropdown" class="select-options">
                <div class="select-option" :class="{ selected: !selectedParentAgentFilter }" @click="selectParentAgentFilter('')">
                  <span class="option-text">全部代理</span>
                </div>
                <div class="select-option" :class="{ selected: selectedParentAgentFilter === 'admin' }" @click="selectParentAgentFilter('admin')">
                  <span class="option-text">admin创建</span>
                </div>
                <div
                  v-for="agent in agentList"
                  :key="agent.id"
                  class="select-option"
                  :class="{ selected: selectedParentAgentFilter === agent.id.toString() }"
                  @click="selectParentAgentFilter(agent.id.toString())"
                >
                  <span class="option-text">{{ agent.username }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <button
            class="reset-filter-btn"
            @click="handleResetFilters"
            v-if="searchKeyword || selectedGameFilter || selectedRoleFilter || selectedStatusFilter || selectedParentAgentFilter"
            title="重置所有筛选条件"
          >
            <i class="icon">🔄</i>
            <span>重置</span>
          </button>
          <button
            class="view-logs-btn"
            @click="viewOperationLogs"
            title="查看操作日志"
          >
            <i class="icon">📋</i>
            <span>操作日志</span>
          </button>
          <div
            class="custom-upload-btn"
            @click="showCreateUserDialog"
          >
            <i class="el-icon-plus"></i>
            <span>新增用户</span>
          </div>
        </div>
      </div>
      </el-card>

      <el-card class="user-mgmt-body-card" shadow="never" :body-style="{ padding: '0' }">
      <div class="user-list-container">
        <div v-if="userListLoading" class="loading-container">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
        </div>
        <div v-else class="content-container">
          <!-- 桌面端表格布局 -->
          <div class="table-container desktop-table">
            <div class="table-scroll">
            <div class="table-header">
              <div class="header-cell id-col">ID</div>
              <div class="header-cell username-col">用户名</div>
              <div class="header-cell parent-col">上级代理</div>
              <div class="header-cell windows-col sort-cell" @click="handleSort('number')">
                <span>窗/绑/活跃</span>
                <span class="sort-icon">{{ sortField === 'number' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕' }}</span>
              </div>
              <div class="header-cell game-col">游戏</div>
              <div class="header-cell expiry-col sort-cell" @click="handleSort('expiry_date')">
                <span>到期时间</span>
                <span class="sort-icon">{{ sortField === 'expiry_date' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕' }}</span>
              </div>
              <div class="header-cell admin-col">角色</div>
              <div class="header-cell actions-col">操作</div>
            </div>
              <div v-if="paginatedUserList.length === 0" class="empty-state">
                <div class="empty-icon">👥</div>
                <div class="empty-text">未找到匹配的用户</div>
                <div class="empty-desc">尝试调整搜索条件或筛选器</div>
              </div>
              <div
                v-else
                v-for="user in paginatedUserList"
                :key="user.id"
                class="table-row"
              >
                <div class="cell id-col">{{ user.id }}</div>
                <div class="cell username-col">{{ user.username }}</div>
                <div class="cell parent-col">{{ user.parentUsername || '-' }}</div>
                <div class="cell windows-col" :title="formatWindowStatsTooltip(user)">
                  {{ formatWindowStats(user) }}
                </div>
                <div class="cell game-col">{{ user.game || '冒险岛枫星' }}</div>
                <div class="cell expiry-col">
                  <span v-if="user.is_deleted === 1" class="expiry-badge deleted">
                    已删除
                  </span>
                  <span v-else-if="user.username === 'admin'" class="expiry-badge admin-no-expiry">
                    永久有效
                  </span>
                  <span v-else :class="['expiry-badge', getExpiryStatus(user.expiry_date)]">
                    {{ formatExpiryDate(user.expiry_date) }}
                  </span>
                </div>
                <div class="cell admin-col">
                  <span :class="['admin-badge', getUserTypeClass(user.is_admin)]">
                    {{ getUserTypeText(user.is_admin) }}
                  </span>
                </div>
                <div class="cell actions-col">
                  <div class="user-actions">
                    <button type="button" class="action-btn primary" @click="showEditUserDialog(user)">
                      编辑
                    </button>
                    <el-dropdown trigger="click" @command="(cmd) => handleUserAction(cmd, user)">
                      <button type="button" class="action-btn action-btn--menu">
                        更多
                        <span class="action-menu-caret">▼</span>
                      </button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item command="password">修改密码</el-dropdown-item>
                          <el-dropdown-item command="expire" :disabled="user.id === userInfo.id">
                            设置过期
                          </el-dropdown-item>
                          <el-dropdown-item command="delete" :disabled="user.id === userInfo.id" divided>
                            删除
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端卡片布局 -->
          <div class="mobile-cards">
            <div v-if="paginatedUserList.length === 0" class="empty-state">
              <div class="empty-icon">👥</div>
              <div class="empty-text">{{ userList.length === 0 ? '暂无用户数据' : '未找到匹配的用户' }}</div>
              <div class="empty-desc">{{ userList.length === 0 ? '点击右上角"新增用户"按钮添加用户' : '尝试输入其他关键词搜索' }}</div>
            </div>
            <div v-else v-for="user in paginatedUserList" :key="user.id" class="user-card">
              <div class="card-header">
                <div class="user-basic-info">
                  <h4 class="username">{{ user.username }}</h4>
                  <span class="user-id">ID: {{ user.id }}</span>
                </div>
                <span :class="['admin-badge', getUserTypeClass(user.is_admin)]">
                  {{ getUserTypeText(user.is_admin) }}
                </span>
              </div>
              
              <div class="card-body">
                <!-- 窗口信息行 -->
                <div class="window-info-row">
                  <span class="window-stat window-stat--combined">
                    <span class="stat-label">窗/绑/活跃:</span>
                    <span class="stat-value">{{ formatWindowStats(user) }}</span>
                  </span>
                </div>
                
                <!-- 游戏信息 -->
                <div class="game-info">
                  <span class="info-label">游戏:</span>
                  <span class="info-value">{{ user.game || '冒险岛枫星' }}</span>
                </div>


                <!-- 上级代理信息 -->
                <div class="parent-info">
                  <span class="info-label">上级代理:</span>
                  <span class="info-value">{{ user.parentUsername || '-' }}</span>
                </div>
                
                <!-- 到期时间 -->
                <div class="expiry-info">
                  <span class="info-label">到期:</span>
                  <span v-if="user.is_deleted === 1" class="expiry-badge deleted">
                    已删除
                  </span>
                  <span v-else-if="user.username === 'admin'" class="expiry-badge admin-no-expiry">
                    永久有效
                  </span>
                  <span v-else :class="['expiry-badge', getExpiryStatus(user.expiry_date)]">
                    {{ formatExpiryDate(user.expiry_date) }}
                  </span>
                </div>
              </div>
              
              <div class="card-actions">
                <button type="button" class="action-btn primary" @click="showEditUserDialog(user)">
                  编辑
                </button>
                <el-dropdown trigger="click" @command="(cmd) => handleUserAction(cmd, user)">
                  <button type="button" class="action-btn action-btn--menu">
                    更多
                    <span class="action-menu-caret">▼</span>
                  </button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item command="password">修改密码</el-dropdown-item>
                      <el-dropdown-item command="expire" :disabled="user.id === userInfo.id">
                        设置过期
                      </el-dropdown-item>
                      <el-dropdown-item command="delete" :disabled="user.id === userInfo.id" divided>
                        删除
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </div>
          
          <!-- 分页控件 -->
          <div v-if="paginatedUserList.length > 0" class="pagination-container">
            <div class="pagination-left">
              <div class="pagination-info">
                共 {{ displayTotalUsers }} 条，第 {{ currentPage }} / {{ displayTotalPages }} 页
              </div>
              <div class="pagination-page-size">
                <span class="page-size-label">每页显示</span>
                <el-select
                  v-model="pageSize"
                  class="page-size-select"
                  @change="handlePageSizeChange"
                >
                  <el-option
                    v-for="n in pageSizeOptions"
                    :key="n"
                    :label="`${n} 条`"
                    :value="n"
                  />
                </el-select>
              </div>
            </div>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                :disabled="currentPage === 1"
                @click="handlePrevPage"
              >
                上一页
              </button>
              <div class="pagination-pages">
                <button
                  v-for="page in displayTotalPages"
                  :key="page"
                  v-show="showPageNumber(page)"
                  class="pagination-page"
                  :class="{ active: page === currentPage }"
                  @click="handlePageChange(page)"
                >
                  {{ page }}
                </button>
              </div>
              <button
                class="pagination-btn"
                :disabled="currentPage === displayTotalPages"
                @click="handleNextPage"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
      </el-card>

      <!-- 创建用户模态框 -->
      <div v-if="showCreateUserDialogRef" class="modal-overlay" @click="closeCreateUserDialog">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h3>新增用户</h3>
            <button class="close-btn" @click="closeCreateUserDialog">×</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <div class="form-label-row">
                <label>用户名</label>
                <button 
                  type="button"
                  class="btn-generate" 
                  @click="generateAccountPassword"
                  :disabled="userFormLoading"
                  title="一键随机生成账号+密码并复制到剪切板"
                >
                  🎲 随机生成
                </button>
              </div>
              <input 
                v-model="userForm.username" 
                type="text" 
                placeholder="请输入用户名（至少3个字符）"
                :disabled="userFormLoading"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>密码</label>
              <input 
                v-model="userForm.password" 
                type="text" 
                placeholder="请输入密码（至少6个字符）"
                :disabled="userFormLoading"
                class="form-input"
              />
            </div>
            <!-- 普通用户才需要的字段 -->
            <div v-if="userForm.is_admin !== 2" class="form-group">
              <label>窗口数量</label>
              <input 
                v-model.number="userForm.number" 
                type="number" 
                min="1" 
                max="10000"
                :disabled="userFormLoading"
                class="form-input"
              />
            </div>
            <div v-if="userForm.is_admin !== 2" class="form-group">
              <label>时长（天）</label>
              <input 
                v-model.number="userForm.durationDays" 
                type="number" 
                min="1" 
                max="365"
                :disabled="userFormLoading"
                class="form-input"
                placeholder="默认30天"
              />
            </div>
            <div v-if="userForm.is_admin !== 2" class="form-group">
              <label>游戏</label>
              <div class="custom-select" :class="{ disabled: userFormLoading }">
                <div class="select-trigger" @click="toggleCreateGameDropdown" :class="{ active: showCreateGameDropdown }">
                  <span class="select-value">{{ userForm.game }}</span>
                  <i class="select-arrow" :class="{ rotated: showCreateGameDropdown }">▼</i>
                </div>
                <div v-if="showCreateGameDropdown" class="select-options">
                  <div
                    v-for="game in gameList"
                    :key="game.id"
                    class="select-option"
                    :class="{ selected: userForm.game === game.gameName }"
                    @click="selectCreateGame(game.gameName)"
                  >
                    <span class="option-text">{{ game.gameName }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- 用户类型选择 -->
            <div class="form-group">
              <label>用户类型</label>
              <div class="custom-select" :class="{ disabled: userFormLoading }">
                <div class="select-trigger" @click="toggleCreateUserTypeDropdown" :class="{ active: showCreateUserTypeDropdown }">
                  <span class="select-value">{{ getUserTypeText(userForm.is_admin) }}</span>
                  <i class="select-arrow" :class="{ rotated: showCreateUserTypeDropdown }">▼</i>
                </div>
                <div v-if="showCreateUserTypeDropdown" class="select-options">
                  <div 
                    class="select-option"
                    :class="{ selected: userForm.is_admin === 0 }"
                    @click="selectCreateUserType(0)"
                  >
                    <span class="option-text">普通用户</span>
                  </div>
                  <div 
                    v-if="userInfo.is_admin === 1"
                    class="select-option"
                    :class="{ selected: userForm.is_admin === 2 }"
                    @click="selectCreateUserType(2)"
                  >
                    <span class="option-text">代理</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- 备注 -->
            <div class="form-group">
              <label>备注</label>
              <textarea 
                v-model="userForm.remark" 
                placeholder="请输入备注信息（可选）"
                :disabled="userFormLoading"
                class="form-textarea"
                rows="3"
                maxlength="500"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn secondary" @click="closeCreateUserDialog" :disabled="userFormLoading">
              取消
            </button>
            <button class="btn primary" @click="handleCreateUser" :disabled="userFormLoading">
              <span v-if="userFormLoading" class="btn-spinner"></span>
              创建
            </button>
          </div>
        </div>
      </div>

      <!-- 编辑用户模态框 -->
      <div v-if="showEditUserDialogRef" class="modal-overlay" @click="closeEditUserDialog">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h3>编辑用户</h3>
            <button class="close-btn" @click="closeEditUserDialog">×</button>
          </div>
          <div class="modal-body">
            <div class="user-info">
              <p><strong>用户名：</strong>{{ currentEditUser?.username }}</p>
              <p><strong>当前窗口数：</strong>{{ currentEditUser?.number }}</p>
              <p><strong>当前到期时间：</strong>{{ formatExpiryDate(currentEditUser?.expiry_date) }}</p>
            </div>
            
            <div class="form-group">
              <label>操作类型</label>
              <div class="custom-select" :class="{ disabled: userFormLoading }">
                <div class="select-trigger" @click="toggleOperationTypeDropdown" :class="{ active: showOperationTypeDropdown }">
                  <span class="select-value">{{ getOperationTypeLabel(editUserForm.operationType) }}</span>
                  <i class="select-arrow" :class="{ rotated: showOperationTypeDropdown }">▼</i>
                </div>
                <div v-if="showOperationTypeDropdown" class="select-options">
                  <div 
                    class="select-option" 
                    :class="{ selected: editUserForm.operationType === 'RENEW' }"
                    @click="selectOperationType('RENEW')"
                  >
                    <span class="option-text">续费时长（窗口不变）</span>
                    <span class="option-desc">在当前到期时间基础上延长指定天数</span>
                  </div>
                  <div 
                    class="select-option" 
                    :class="{ selected: editUserForm.operationType === 'EXPAND_WITH_PAYMENT' }"
                    @click="selectOperationType('EXPAND_WITH_PAYMENT')"
                  >
                    <span class="option-text">增加窗口且交钱</span>
                    <span class="option-desc">指定新增窗口数量，按比例平摊时间</span>
                  </div>
                  <div 
                    class="select-option" 
                    :class="{ selected: editUserForm.operationType === 'ADJUST_CAPACITY' }"
                    @click="selectOperationType('ADJUST_CAPACITY')"
                  >
                    <span class="option-text">调整窗口容量（扩容或缩容）</span>
                    <span class="option-desc">指定改完后的总窗口数，时长按比例调整</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="editUserForm.operationType === 'EXPAND_WITH_PAYMENT'" class="form-group">
              <label>新增窗口数量</label>
              <input 
                v-model.number="editUserForm.newWindowCount" 
                type="number" 
                min="1" 
                max="50"
                :disabled="userFormLoading"
                class="form-input"
                placeholder="本次新增的窗口数量"
              />
            </div>
            
            <div v-if="editUserForm.operationType === 'ADJUST_CAPACITY'" class="form-group">
              <label>调整后的总窗口数量</label>
              <input 
                v-model.number="editUserForm.newWindowCount" 
                type="number" 
                min="1" 
                max="50"
                :disabled="userFormLoading"
                class="form-input"
                placeholder="调整完成后的总窗口数量"
              />
            </div>
            
            <div v-if="editUserForm.operationType !== 'ADJUST_CAPACITY'" class="form-group">
              <label>时长（天）</label>
              <input 
                v-model.number="editUserForm.durationDays" 
                type="number" 
                min="1" 
                max="365"
                :disabled="userFormLoading"
                class="form-input"
                placeholder="默认30天"
              />
            </div>
            
            <div class="form-group">
              <label>游戏</label>
              <div class="custom-select" :class="{ disabled: userFormLoading }">
                <div class="select-trigger" @click="toggleEditGameDropdown" :class="{ active: showEditGameDropdown }">
                  <span class="select-value">{{ editUserForm.game }}</span>
                  <i class="select-arrow" :class="{ rotated: showEditGameDropdown }">▼</i>
                </div>
                <div v-if="showEditGameDropdown" class="select-options">
                  <div
                    v-for="game in gameList"
                    :key="game.id"
                    class="select-option"
                    :class="{ selected: editUserForm.game === game.gameName }"
                    @click="selectEditGame(game.gameName)"
                  >
                    <span class="option-text">{{ game.gameName }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="editUserForm.operationType" class="operation-description">
              <div class="description-title">操作说明：</div>
              <div class="description-content">
                <span v-if="editUserForm.operationType === 'RENEW'">
                  续费操作会在当前到期时间基础上延长指定天数，窗口数量保持不变。
                </span>
                <span v-else-if="editUserForm.operationType === 'EXPAND_WITH_PAYMENT'">
                  增加窗口且交钱：指定本次新增的窗口数量，会按比例平摊时间。<br/>
                  计算公式：(原窗口数×剩余时长 + 新增窗口数×新增时长) ÷ (原窗口数+新增窗口数)
                </span>
                <span v-else-if="editUserForm.operationType === 'ADJUST_CAPACITY'">
                  调整窗口容量：指定调整后的总窗口数量，原有时长会重新分配。<br/>
                  计算公式：当前剩余时长 × 原窗口数 ÷ 新窗口数
                </span>
              </div>
            </div>
            
            <!-- 备注 -->
            <div class="form-group">
              <label>备注</label>
              <textarea 
                v-model="editUserForm.remark" 
                placeholder="请输入备注信息（可选）"
                :disabled="userFormLoading"
                class="form-textarea"
                rows="3"
                maxlength="500"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn secondary" @click="closeEditUserDialog" :disabled="userFormLoading">
              取消
            </button>
            <button class="btn primary" @click="handleEditUser" :disabled="userFormLoading">
              <span v-if="userFormLoading" class="btn-spinner"></span>
              确认操作
            </button>
          </div>
        </div>
      </div>

      <!-- 重置密码模态框 -->
      <div v-if="showResetPasswordDialogRef" class="modal-overlay" @click="closeResetPasswordDialog">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h3>重置密码</h3>
            <button class="close-btn" @click="closeResetPasswordDialog">×</button>
          </div>
          <div class="modal-body">
            <div class="reset-password-info">
              <p>为用户 <strong>{{ currentEditUser?.username }}</strong> 重置密码</p>
            </div>
            <div class="form-group">
              <label>新密码</label>
              <input 
                v-model="resetPasswordForm.new_password" 
                type="text" 
                placeholder="请输入新密码（至少6个字符）"
                :disabled="userFormLoading"
                class="form-input"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn secondary" @click="closeResetPasswordDialog" :disabled="userFormLoading">
              取消
            </button>
            <button class="btn warning" @click="handleResetPassword" :disabled="userFormLoading">
              <span v-if="userFormLoading" class="btn-spinner"></span>
              重置密码
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import userAPI from '../api/users'
import gamesAPI from '../api/games'

/** 创建账号 / 筛选 可选的冒险岛游戏 */
const ADVENTURE_ISLAND_GAMES = ['冒险岛枫星', '冒险岛韩服']

export default {
  name: 'UserManagement',
  setup() {
    const router = useRouter()
    const userInfo = ref({
      username: '',
      is_admin: 0
    })

    // 用户列表相关
    const userList = ref([])
    const userListLoading = ref(false)
    const searchKeyword = ref('')

    const isMobileToolbar = ref(typeof window !== 'undefined' && window.innerWidth <= 768)
    const mobileFiltersExpanded = ref(true)

    const updateMobileToolbar = () => {
      const mobile = window.innerWidth <= 768
      isMobileToolbar.value = mobile
      if (!mobile) {
        mobileFiltersExpanded.value = true
      }
    }

    // 游戏筛选相关
    const gameList = ref([])
    const selectedGameFilter = ref('')

    const showGameFilterDropdown = ref(false)

    // 角色筛选相关
    const selectedRoleFilter = ref('')
    const showRoleFilterDropdown = ref(false)

    // 状态筛选相关
    const selectedStatusFilter = ref('')
    const showStatusFilterDropdown = ref(false)

    // 上级代理筛选相关（仅管理员可用）
    const selectedParentAgentFilter = ref('')
    const showParentAgentFilterDropdown = ref(false)
    const agentList = ref([])

    const activeFilterCount = computed(() => {
      let count = 0
      if (selectedGameFilter.value) count++
      if (selectedRoleFilter.value) count++
      if (selectedStatusFilter.value) count++
      if (selectedParentAgentFilter.value) count++
      return count
    })

    // 分页相关（后端分页）
    const PAGE_SIZE_STORAGE_KEY = 'userMgmtPageSize'
    const pageSizeOptions = [10, 20, 50, 100]
    const readStoredPageSize = () => {
      try {
        const v = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10)
        if (pageSizeOptions.includes(v)) return v
      } catch {
        /* ignore */
      }
      return 20
    }
    const currentPage = ref(1)
    const pageSize = ref(readStoredPageSize())
    const totalUsers = ref(0)
    const totalPages = ref(0)

    // 对话框控制
    const showCreateUserDialogRef = ref(false)
    const showEditUserDialogRef = ref(false)
    const showResetPasswordDialogRef = ref(false)
    
    // 下拉框控制
    const showOperationTypeDropdown = ref(false)
    const showCreateGameDropdown = ref(false)
    const showEditGameDropdown = ref(false)
    const showCreateUserTypeDropdown = ref(false)
    

    // 表单数据
    const userForm = ref({
      username: '',
      password: '',
      number: 1,
      durationDays: 30,
      game: '冒险岛枫星',
      is_admin: 0,
      remark: ''
    })

    const editUserForm = ref({
      operationType: 'RENEW',
      newWindowCount: null,
      durationDays: 30,
      game: '冒险岛枫星',
      is_admin: 0,
      remark: ''
    })

    const resetPasswordForm = ref({
      new_password: ''
    })

    const currentEditUser = ref(null)
    const userFormLoading = ref(false)

    // 当前页的用户列表（后端分页+搜索+角色筛选+状态筛选+游戏筛选+排序）
    const paginatedUserList = computed(() => {
      // 直接使用后端返回的数据（所有筛选和排序都由后端处理）
      return userList.value
    })

    // 计算实际显示的总页数
    const displayTotalPages = computed(() => {
      // 使用后端返回的总页数（已包含所有筛选和排序结果）
      return totalPages.value
    })

    // 计算实际显示的总数量
    const displayTotalUsers = computed(() => {
      // 使用后端返回的总数量（已包含所有筛选和排序结果）
      return totalUsers.value
    })

    // 计算上级代理筛选的显示文本
    const parentAgentFilterDisplayText = computed(() => {
      if (!selectedParentAgentFilter.value) {
        return '选择上级代理'
      }
      if (selectedParentAgentFilter.value === 'admin') {
        return 'admin创建'
      }
      // 查找对应的代理用户名
      const agent = agentList.value.find(a => a.id.toString() === selectedParentAgentFilter.value)
      return agent ? agent.username : selectedParentAgentFilter.value
    })

    // 搜索处理函数（已由 watch 监听器处理，这里保留以防万一）
    const handleSearch = () => {
      // 搜索由 watch 监听器自动触发后端查询
      // 这里不需要做任何事情
    }

    // 游戏筛选处理函数
    const handleGameFilter = () => {
      // 筛选时重置到第一页，并重新加载数据（后端筛选）
      currentPage.value = 1
      loadUserList()
    }

    const sortField = ref('')
    const sortOrder = ref('asc')
    const handleSort = (field) => {
      if (sortField.value === field) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortOrder.value = 'asc'
      }
      currentPage.value = 1
      // 调用后台重新加载数据
      loadUserList()
    }

    // 分页控制方法
    const handlePageChange = (page) => {
      if (page < 1 || page > displayTotalPages.value) return
      currentPage.value = page
      // 重新加载数据（所有筛选和排序都由后端处理）
      loadUserList()
    }

    const handlePrevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        // 重新加载数据（所有筛选和排序都由后端处理）
        loadUserList()
      }
    }

    const handleNextPage = () => {
      if (currentPage.value < displayTotalPages.value) {
        currentPage.value++
        // 重新加载数据（所有筛选和排序都由后端处理）
        loadUserList()
      }
    }

    const handlePageSizeChange = (val) => {
      const n = Number(val)
      if (!pageSizeOptions.includes(n)) return
      pageSize.value = n
      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n))
      } catch {
        /* ignore */
      }
      currentPage.value = 1
      loadUserList()
    }

    // 判断是否显示页码（智能分页显示）
    const showPageNumber = (page) => {
      if (displayTotalPages.value <= 7) return true // 总页数少于等于7页，全部显示
      if (page === 1 || page === displayTotalPages.value) return true // 始终显示首页和尾页
      if (page >= currentPage.value - 2 && page <= currentPage.value + 2) return true // 显示当前页前后2页
      return false
    }

    // 用户管理方法（后端分页 + 搜索 + 角色筛选 + 状态筛选 + 游戏筛选）
    const loadUserList = async () => {
      userListLoading.value = true
      try {
        // 传递搜索关键词和角色筛选到后端
        const keyword = searchKeyword.value.trim() || null

        // 将角色名称转换为角色值
        let roleValue = null
        if (selectedRoleFilter.value) {
          const roleMap = {
            '普通用户': 0,
            '超级管理员': 1,
            '代理': 2,
            '配置管理员': 3
          }
          roleValue = roleMap[selectedRoleFilter.value]
        }

        // 将状态名称转换为状态值
        let statusValue = null
        if (selectedStatusFilter.value) {
          const statusMap = {
            '正常': 0,
            '已删除': 1
          }
          statusValue = statusMap[selectedStatusFilter.value]
        }

        // 游戏筛选
        const gameFilter = selectedGameFilter.value || null

        // 上级代理筛选（仅管理员可用）
        let parentAgentId = null
        if (selectedParentAgentFilter.value) {
          if (selectedParentAgentFilter.value === 'admin') {
            parentAgentId = -1 // -1表示查询admin创建的用户
          } else {
            parentAgentId = parseInt(selectedParentAgentFilter.value)
          }
        }

        // 排序参数
        const sortParam = sortField.value ? {
          sortField: sortField.value,
          sortOrder: sortOrder.value
        } : null

        const response = await userAPI.getUserList(currentPage.value, pageSize.value, keyword, roleValue, statusValue, gameFilter, parentAgentId, sortParam)
        const data = response.data

        console.log('后端返回的数据:', data, '搜索关键词:', keyword, '角色:', roleValue)

        // 检查返回的数据格式
        if (data && typeof data === 'object' && 'list' in data) {
          // 后端分页格式
          userList.value = data.list || []
          totalUsers.value = data.total || 0
          totalPages.value = data.totalPages || 0
          console.log('分页数据 - 列表长度:', userList.value.length, '总数:', totalUsers.value, '总页数:', totalPages.value)
        } else {
          // 兼容旧格式（不分页）
          userList.value = data || []
          totalUsers.value = userList.value.length
          totalPages.value = 1
          console.log('非分页数据 - 列表长度:', userList.value.length)
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        ElMessage.error('获取用户列表失败')
      } finally {
        userListLoading.value = false
      }
    }

    // 读取cookie的辅助函数
    const getCookie = (name) => {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(';').shift())
      }
      return null
    }

    // 加载游戏列表
    const loadGameList = async () => {
      try {
        console.log('开始加载游戏列表...')
        // 从后端获取游戏列表
        const response = await gamesAPI.getGameRecommendations()
        console.log('游戏列表API响应:', response)

        if (response.success && response.data) {
          const games = response.data.games || []
          // 前端兜底：只保留冒险岛游戏，避免数据库残留旧游戏导致下拉仍出现尤弥尔
          const filteredGames = games.filter(g => ADVENTURE_ISLAND_GAMES.includes(g.gameName))
          gameList.value = (filteredGames.length > 0 ? filteredGames : ADVENTURE_ISLAND_GAMES.map((name, i) => ({
            id: i + 1,
            gameName: name
          }))).map(game => ({
            id: game.id,
            gameName: game.gameName
          }))
          console.log('加载游戏列表成功:', gameList.value)
        } else {
          console.warn('获取游戏列表失败，响应格式不正确，使用默认列表', response)
          gameList.value = ADVENTURE_ISLAND_GAMES.map((name, i) => ({
            id: i + 1,
            gameName: name
          }))
        }
      } catch (error) {
        console.error('加载游戏列表失败，发生异常:', error)
        console.error('错误详情:', error.response || error.message)
        gameList.value = ADVENTURE_ISLAND_GAMES.map((name, i) => ({
          id: i + 1,
          gameName: name
        }))
      }

      // 从cookie恢复用户上次选择的游戏筛选
      const savedGameFilter = getCookie('gameFilter')
      if (savedGameFilter && ADVENTURE_ISLAND_GAMES.includes(savedGameFilter)) {
        selectedGameFilter.value = savedGameFilter
      } else {
        selectedGameFilter.value = ADVENTURE_ISLAND_GAMES[0]
      }
    }

    // 加载代理列表（仅管理员可用）
    const loadAgentList = async () => {
      if (userInfo.value.is_admin !== 1) {
        return // 非管理员不加载代理列表
      }

      try {
        const response = await userAPI.getAgentList()

        if ((response.success || response.code === 200) && response.data) {
          agentList.value = response.data
        } else {
          agentList.value = []
        }
      } catch (error) {
        agentList.value = []
      }
    }

    // 生成随机账号和密码
    const generateAccountPassword = () => {
      // 生成前4位英文（小写）
      const letters = 'abcdefghijklmnopqrstuvwxyz'
      let usernamePrefix = ''
      for (let i = 0; i < 4; i++) {
        usernamePrefix += letters.charAt(Math.floor(Math.random() * letters.length))
      }
      
      // 生成后4位数字
      const numbers = '0123456789'
      let usernameSuffix = ''
      for (let i = 0; i < 4; i++) {
        usernameSuffix += numbers.charAt(Math.floor(Math.random() * numbers.length))
      }
      
      // 组合账号：前4位英文 + 后4位数字
      const username = usernamePrefix + usernameSuffix
      
      // 生成6位数字密码
      let password = ''
      for (let i = 0; i < 6; i++) {
        password += numbers.charAt(Math.floor(Math.random() * numbers.length))
      }
      
      // 填充到表单
      userForm.value.username = username
      userForm.value.password = password
      
      // 复制到剪切板：格式为 "账号：\n密码"
      const clipboardText = `账号：${username}\n密码：${password}`
      navigator.clipboard.writeText(clipboardText).then(() => {
        // 使用 nextTick 确保 DOM 已准备好，避免 MutationObserver 错误
        nextTick(() => {
          ElMessage.success(`账号密码已生成并复制到剪切板：${username}:${password}`)
        })
      }).catch(err => {
        console.error('复制到剪切板失败:', err)
        // 如果复制失败，至少显示成功消息
        nextTick(() => {
          ElMessage.success(`账号密码已生成：${username}:${password}`)
        })
      })
    }

    const showCreateUserDialog = () => {
      // 获取默认游戏（优先使用游戏列表的第一个，否则使用固定值）
      const defaultGame = gameList.value.length > 0 ? gameList.value[0].gameName : '冒险岛枫星'

      // 优先从cookie获取游戏筛选，其次从localStorage，最后使用默认值
      const savedGameFilter = getCookie('gameFilter')
      const savedGame = savedGameFilter || localStorage.getItem('selectedGame') || defaultGame
      userForm.value = {
        username: '',
        password: '',
        number: 1,
        durationDays: 30,
        game: savedGame,
        is_admin: 0,
        remark: ''
      }
      showCreateUserDialogRef.value = true
    }

    const closeCreateUserDialog = () => {
      showCreateUserDialogRef.value = false
      showCreateGameDropdown.value = false
    }

    const showEditUserDialog = (user) => {
      if (!user) return
      currentEditUser.value = user
      // 优先从cookie获取游戏筛选，其次从localStorage，最后使用用户当前游戏或默认值
      const savedGameFilter = getCookie('gameFilter')
      const savedGame = savedGameFilter || localStorage.getItem('selectedGame') || user.game || '冒险岛枫星'
      editUserForm.value = {
        operationType: 'RENEW',
        newWindowCount: user.number,
        durationDays: 30,
        game: savedGame,
        is_admin: user.is_admin,
        remark: ''
      }
      showEditUserDialogRef.value = true
    }

    const closeEditUserDialog = () => {
      showEditUserDialogRef.value = false
      showOperationTypeDropdown.value = false
      showEditGameDropdown.value = false
    }

    const showResetPasswordDialog = (user) => {
      if (!user) return
      currentEditUser.value = user
      resetPasswordForm.value = {
        new_password: ''
      }
      showResetPasswordDialogRef.value = true
    }

    const closeResetPasswordDialog = () => {
      showResetPasswordDialogRef.value = false
    }

    // 查看操作日志 - 跳转到操作日志页面
    const viewOperationLogs = () => {
      router.push('/dashboard/users/operation-logs')
    }

    const handleCreateUser = async () => {
      userFormLoading.value = true
      try {
        // 直接使用表单数据，已经在选择用户类型时处理好了
        const submitData = { ...userForm.value }
        
        // 保存用户选择的游戏到localStorage（仅普通用户）
        if (submitData.is_admin === 0 && submitData.game) {
          localStorage.setItem('selectedGame', submitData.game)
        }
        
        await userAPI.createUser(submitData)
        ElMessage.success('用户创建成功')
        showCreateUserDialogRef.value = false
        await loadUserList()
      } catch (error) {
        console.error('创建用户失败:', error)
        ElMessage.error(error.response?.data?.error || error.response?.data?.message || '创建用户失败')
      } finally {
        userFormLoading.value = false
      }
    }

    const handleEditUser = async () => {
      userFormLoading.value = true
      try {
        // 保存用户选择的游戏到localStorage
        localStorage.setItem('selectedGame', editUserForm.value.game)
        
        await userAPI.editUser(currentEditUser.value.id, editUserForm.value)
        ElMessage.success('用户编辑成功')
        showEditUserDialogRef.value = false
        await loadUserList()
      } catch (error) {
        console.error('编辑用户失败:', error)
        ElMessage.error(error.response?.data?.error || '编辑用户失败')
      } finally {
        userFormLoading.value = false
      }
    }

    const handleUpdateUser = async () => {
      userFormLoading.value = true
      try {
        await userAPI.updateUser(currentEditUser.value.id, editUserForm.value)
        ElMessage.success('用户更新成功')
        showEditUserDialogRef.value = false
        await loadUserList()
      } catch (error) {
        console.error('更新用户失败:', error)
        ElMessage.error(error.response?.data?.error || '更新用户失败')
      } finally {
        userFormLoading.value = false
      }
    }

    const handleResetPassword = async () => {
      userFormLoading.value = true
      try {
        await userAPI.resetUserPassword(currentEditUser.value.id, resetPasswordForm.value.new_password)
        ElMessage.success('密码重置成功')
        showResetPasswordDialogRef.value = false
      } catch (error) {
        console.error('重置密码失败:', error)
        ElMessage.error(error.response?.data?.error || '重置密码失败')
      } finally {
        userFormLoading.value = false
      }
    }

    const handleUserAction = (command, user) => {
      switch (command) {
        case 'password':
          showResetPasswordDialog(user)
          break
        case 'expire':
          handleExpireUser(user)
          break
        case 'delete':
          handleMarkUserDeleted(user)
          break
        default:
          break
      }
    }

    const handleExpireUser = async (user) => {
      if (!user) {
        ElMessage.error('用户数据无效')
        return
      }
      try {
        await ElMessageBox.confirm(
          `确定要将用户 "${user.username || '未知用户'}" 设为过期吗？该用户将无法登录，但数据会保留。`,
          '设为过期确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )

        await userAPI.deleteUser(user.id)
        ElMessage.success('用户已设为过期')
        await loadUserList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('设置过期失败:', error)
          ElMessage.error(error.response?.data?.error || '设置过期失败')
        }
      }
    }

    const handleMarkUserDeleted = async (user) => {
      if (!user) {
        ElMessage.error('用户数据无效')
        return
      }
      try {
        await ElMessageBox.confirm(
          `确定要删除用户 "${user.username || '未知用户'}" 吗？该用户将被标记为删除状态，无法在系统中显示。`,
          '删除账号确认',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'error',
          }
        )

        await userAPI.markUserAsDeleted(user.id)
        ElMessage.success('用户已删除')
        await loadUserList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除用户失败:', error)
          ElMessage.error(error.response?.data?.error || '删除用户失败')
        }
      }
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '-'
        return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour12: false })
      } catch (error) {
        console.error('日期格式化失败:', error)
        return '-'
      }
    }

    // 格式化到期时间
    const DEMO_WINDOW_STATS_USERNAME = 'liuce19962'

    const getDisplayWindowStats = (user) => {
      if (user?.username === DEMO_WINDOW_STATS_USERNAME) {
        return { limit: 100, bound: 9, active: 9 }
      }
      return {
        limit: user?.number ?? 0,
        bound: user?.bound_window_count || 0,
        active: user?.active_window_count || 0
      }
    }

    const formatWindowStats = (user) => {
      const { limit, bound, active } = getDisplayWindowStats(user)
      return `${limit}/${bound}/${active}`
    }

    const formatWindowStatsTooltip = (user) => {
      const { limit, bound, active } = getDisplayWindowStats(user)
      return `窗口 ${limit} / 绑定 ${bound} / 活跃 ${active}`
    }

    const formatExpiryDate = (dateString) => {
      if (!dateString) return '未设置'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '无效日期'
        const now = new Date()
        const isExpired = date < now
        return (isExpired ? '已过期 ' : '') + 
               date.toLocaleDateString('zh-CN') + ' ' + 
               date.toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        console.error('到期日期格式化失败:', error)
        return '格式错误'
      }
    }

    // 获取到期状态
    const getExpiryStatus = (dateString) => {
      if (!dateString) return 'unknown'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'unknown'
        const now = new Date()
        const diffTime = date - now
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (diffDays < 0) return 'expired'
        if (diffDays <= 3) return 'warning'
        if (diffDays <= 7) return 'soon'
        return 'normal'
      } catch (error) {
        return 'unknown'
      }
    }

    // 获取用户类型文本
    const getUserTypeText = (isAdmin) => {
      if (isAdmin === 1) return '超级管理员'
      if (isAdmin === 2) return '代理'
      if (isAdmin === 3) return '配置管理员'
      return '普通用户'
    }

    // 获取用户类型样式类
    const getUserTypeClass = (isAdmin) => {
      if (isAdmin === 1) return 'admin'
      if (isAdmin === 2) return 'agent'
      if (isAdmin === 3) return 'config-admin'
      return 'normal'
    }

    // 操作类型变化时的处理
    const onOperationTypeChange = () => {
      if (editUserForm.value.operationType === 'RENEW') {
        editUserForm.value.newWindowCount = null
      } else if (editUserForm.value.operationType === 'EXPAND_WITH_PAYMENT') {
        // 新增窗口数量，默认1个
        editUserForm.value.newWindowCount = 1
      } else if (editUserForm.value.operationType === 'ADJUST_CAPACITY') {
        // 调整后的总窗口数量，默认当前数量
        editUserForm.value.newWindowCount = currentEditUser.value ? currentEditUser.value.number : 1
      }
    }

    // 下拉框操作方法
    const toggleOperationTypeDropdown = () => {
      if (userFormLoading.value) return
      showOperationTypeDropdown.value = !showOperationTypeDropdown.value
      showEditGameDropdown.value = false
    }

    const toggleCreateGameDropdown = () => {
      if (userFormLoading.value) return
      showCreateGameDropdown.value = !showCreateGameDropdown.value
    }

    const toggleEditGameDropdown = () => {
      if (userFormLoading.value) return
      showEditGameDropdown.value = !showEditGameDropdown.value
      showOperationTypeDropdown.value = false
    }

    const toggleCreateUserTypeDropdown = () => {
      if (userFormLoading.value) return
      showCreateUserTypeDropdown.value = !showCreateUserTypeDropdown.value
    }

    // 游戏筛选下拉框操作
    const toggleGameFilterDropdown = () => {
      if (userListLoading.value) return
      showGameFilterDropdown.value = !showGameFilterDropdown.value
      showRoleFilterDropdown.value = false
      showStatusFilterDropdown.value = false
      showParentAgentFilterDropdown.value = false
    }

    const selectGameFilter = (gameName) => {
      selectedGameFilter.value = gameName
      showGameFilterDropdown.value = false
      handleGameFilter()

      // 保存用户选择的游戏筛选到cookie
      if (gameName) {
        document.cookie = `gameFilter=${encodeURIComponent(gameName)}; path=/; max-age=${30 * 24 * 60 * 60}` // 30天
      } else {
        document.cookie = `gameFilter=; path=/; max-age=0` // 删除cookie
      }
    }

    // 角色筛选下拉框操作
    const toggleRoleFilterDropdown = () => {
      if (userListLoading.value) return
      showRoleFilterDropdown.value = !showRoleFilterDropdown.value
      showGameFilterDropdown.value = false
      showStatusFilterDropdown.value = false
      showParentAgentFilterDropdown.value = false
    }

    const selectRoleFilter = (roleName) => {
      selectedRoleFilter.value = roleName
      showRoleFilterDropdown.value = false
      handleRoleFilter()
    }

    const handleRoleFilter = () => {
      // 角色筛选时重置到第一页，并重新加载数据（后端筛选）
      currentPage.value = 1
      loadUserList()
    }

    // 状态筛选下拉框操作
    const toggleStatusFilterDropdown = () => {
      if (userListLoading.value) return
      showStatusFilterDropdown.value = !showStatusFilterDropdown.value
      showGameFilterDropdown.value = false
      showRoleFilterDropdown.value = false
      showParentAgentFilterDropdown.value = false
    }

    const selectStatusFilter = (statusName) => {
      selectedStatusFilter.value = statusName
      showStatusFilterDropdown.value = false
      // watch 监听器会自动触发后端查询
    }

    // 上级代理筛选下拉框操作（仅管理员可用）
    const toggleParentAgentFilterDropdown = () => {
      if (userListLoading.value) return
      showParentAgentFilterDropdown.value = !showParentAgentFilterDropdown.value
      showGameFilterDropdown.value = false
      showRoleFilterDropdown.value = false
      showStatusFilterDropdown.value = false
    }

    const selectParentAgentFilter = (agentId) => {
      selectedParentAgentFilter.value = agentId
      showParentAgentFilterDropdown.value = false
      // watch 监听器会自动触发后端查询
    }

    // 重置所有筛选条件
    const handleResetFilters = () => {
      searchKeyword.value = ''
      selectedGameFilter.value = ''
      selectedRoleFilter.value = ''
      selectedStatusFilter.value = ''
      selectedParentAgentFilter.value = ''
      sortField.value = ''
      sortOrder.value = 'asc'
      currentPage.value = 1

      // 关闭所有下拉框
      showGameFilterDropdown.value = false
      showRoleFilterDropdown.value = false
      showStatusFilterDropdown.value = false
      showParentAgentFilterDropdown.value = false

      // 删除游戏筛选cookie
      document.cookie = `gameFilter=; path=/; max-age=0`

      // 重新加载数据（使用后端分页）
      loadUserList()
    }

    const selectOperationType = (type) => {
      editUserForm.value.operationType = type
      showOperationTypeDropdown.value = false
      onOperationTypeChange()
    }

    const selectCreateGame = (game) => {
      userForm.value.game = game
      showCreateGameDropdown.value = false
    }

    const selectEditGame = (game) => {
      editUserForm.value.game = game
      showEditGameDropdown.value = false
    }

    const selectCreateUserType = (type) => {
      userForm.value.is_admin = type
      showCreateUserTypeDropdown.value = false

      // 获取默认游戏（优先使用游戏列表的第一个，否则使用固定值）
      const defaultGame = gameList.value.length > 0 ? gameList.value[0].gameName : '冒险岛枫星'

      // 如果选择代理，设置固定的默认值（界面隐藏，但后端需要）
      if (type === 2) {
        userForm.value.number = 1
        userForm.value.durationDays = 360  // 固定360天，实际上代理不会过期
        userForm.value.game = defaultGame
      } else {
        // 如果选择普通用户，设置默认值
        userForm.value.number = 1
        userForm.value.durationDays = 30
        userForm.value.game = defaultGame
      }
    }

    const getOperationTypeLabel = (type) => {
      const labels = {
        'RENEW': '续费时长（窗口不变）',
        'EXPAND_WITH_PAYMENT': '增加窗口且交钱',
        'ADJUST_CAPACITY': '调整窗口容量（扩容或缩容）'
      }
      return labels[type] || '请选择操作类型'
    }

    // 点击外部关闭下拉框
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-select')) {
        showOperationTypeDropdown.value = false
        showCreateGameDropdown.value = false
        showEditGameDropdown.value = false
        showCreateUserTypeDropdown.value = false
        showGameFilterDropdown.value = false
        showRoleFilterDropdown.value = false
        showStatusFilterDropdown.value = false
        showParentAgentFilterDropdown.value = false
      }
    }
    

    // 监听搜索关键词变化，触发后端搜索（防抖）
    let searchTimer = null
    watch(searchKeyword, (newVal) => {
      // 清除之前的定时器
      if (searchTimer) {
        clearTimeout(searchTimer)
      }

      // 设置新的定时器，500ms 后执行搜索
      searchTimer = setTimeout(() => {
        currentPage.value = 1 // 重置到第一页
        loadUserList()
      }, 500)
    })

    // 监听状态筛选变化，触发后端查询
    watch(selectedStatusFilter, () => {
      currentPage.value = 1 // 重置到第一页
      loadUserList()
    })

    // 监听上级代理筛选变化，触发后端查询（仅管理员可用）
    watch(selectedParentAgentFilter, () => {
      currentPage.value = 1 // 重置到第一页
      loadUserList()
    })

    onMounted(async () => {
      // 获取当前用户信息
      const user = localStorage.getItem('user')
      if (user) {
        try {
          const parsedUser = JSON.parse(user)
          userInfo.value = {
            username: parsedUser.username || '',
            is_admin: parsedUser.is_admin !== undefined ? parsedUser.is_admin : 0,
            id: parsedUser.id,
            ...parsedUser
          }
        } catch (error) {
          console.error('解析用户信息失败:', error)
        }
      }
      
      // 添加全局点击事件监听
      document.addEventListener('click', handleClickOutside)
      updateMobileToolbar()
      window.addEventListener('resize', updateMobileToolbar)
      
      // 先加载用户列表和游戏列表，然后加载代理列表（因为代理列表依赖userInfo）
      await Promise.all([
        loadUserList(),
        loadGameList()
      ])

      // 确保userInfo已经设置后再加载代理列表
      await loadAgentList()
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', updateMobileToolbar)
    })

    return {
      isMobileToolbar,
      mobileFiltersExpanded,
      activeFilterCount,
      userInfo,
      userList,
      userListLoading,
      searchKeyword,
      handleSearch,
      // 分页相关
      currentPage,
      pageSize,
      pageSizeOptions,
      handlePageSizeChange,
      totalPages,
      totalUsers,
      displayTotalPages,
      displayTotalUsers,
      paginatedUserList,
      handlePageChange,
      handlePrevPage,
      handleNextPage,
      showPageNumber,
      sortField,
      sortOrder,
      handleSort,
      // 对话框
      showCreateUserDialogRef,
      showEditUserDialogRef,
      showResetPasswordDialogRef,
      userForm,
      editUserForm,
      resetPasswordForm,
      currentEditUser,
      userFormLoading,
      loadUserList,
      showCreateUserDialog,
      closeCreateUserDialog,
      generateAccountPassword,
      showEditUserDialog,
      closeEditUserDialog,
      showResetPasswordDialog,
      closeResetPasswordDialog,
      viewOperationLogs,
      handleCreateUser,
      handleEditUser,
      handleUpdateUser,
      handleResetPassword,
      handleUserAction,
      handleExpireUser,
      handleMarkUserDeleted,
      formatDate,
      formatWindowStats,
      formatWindowStatsTooltip,
      formatExpiryDate,
      getExpiryStatus,
      getUserTypeText,
      getUserTypeClass,
      onOperationTypeChange,
      // 游戏筛选相关
      gameList,
      selectedGameFilter,
      showGameFilterDropdown,
      toggleGameFilterDropdown,
      selectGameFilter,
      handleGameFilter,
      loadGameList,
      // 角色筛选相关
      selectedRoleFilter,
      showRoleFilterDropdown,
      toggleRoleFilterDropdown,
      selectRoleFilter,
      handleRoleFilter,
      // 状态筛选相关
      selectedStatusFilter,
      showStatusFilterDropdown,
      toggleStatusFilterDropdown,
      selectStatusFilter,
      // 上级代理筛选相关（仅管理员可用）
      selectedParentAgentFilter,
      showParentAgentFilterDropdown,
      agentList,
      parentAgentFilterDisplayText,
      toggleParentAgentFilterDropdown,
      selectParentAgentFilter,
      loadAgentList,
      // 重置筛选
      handleResetFilters,
      // 下拉框相关
      showOperationTypeDropdown,
      showCreateGameDropdown,
      showEditGameDropdown,
      showCreateUserTypeDropdown,
      toggleOperationTypeDropdown,
      toggleCreateGameDropdown,
      toggleEditGameDropdown,
      toggleCreateUserTypeDropdown,
      selectOperationType,
      selectCreateGame,
      selectEditGame,
      selectCreateUserType,
      getOperationTypeLabel
    }
  }
}
</script>

<style scoped>
.page-content {
  padding: 0;
  height: 100%;
  min-height: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.user-management {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mobile-search-row {
  display: contents;
}

.mobile-filter-toggle {
  display: none;
}

.user-mgmt-toolbar-card {
  flex-shrink: 0;
  margin-bottom: 16px;
  border-radius: 12px;
  overflow: visible;
}

.user-mgmt-toolbar-card :deep(.el-card),
.user-mgmt-toolbar-card :deep(.el-card__body) {
  overflow: visible !important;
}

.user-mgmt-toolbar-card :deep(.el-card__body) {
  padding: 0 !important;
}

.user-mgmt-body-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
}

.user-mgmt-body-card :deep(.el-card__body) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  flex-shrink: 0;
  padding: 16px 20px;
  gap: 16px;
  overflow: visible;
}

/* 搜索区域样式 */
.search-section {
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  min-width: 0;
  overflow: visible;
}

.search-container {
  position: relative;
  width: 240px;
  flex-shrink: 0;
}

.filter-container {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  overflow: visible;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s ease;
  box-sizing: border-box;
  height: 44px;
}

.search-input:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.search-input::placeholder {
  color: #86868b;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #86868b;
  pointer-events: none;
}

.header-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 重置筛选按钮样式 */
.reset-filter-btn {
  background: white;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  height: 44px;
  padding: 0 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.reset-filter-btn:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
  color: #409eff;
}

.reset-filter-btn:active {
  background: #ebeef5;
  border-color: #b3b8c3;
}

.reset-filter-btn .icon {
  font-size: 14px;
  line-height: 1;
}

.reset-filter-btn span {
  font-size: 14px;
}

/* 查看操作日志按钮样式 */
.view-logs-btn {
  background: #67C23A;
  color: white;
  border: none;
  border-radius: 8px;
  height: 44px;
  padding: 0 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.2);
}

.view-logs-btn:hover {
  background: #85ce61;
  box-shadow: 0 4px 8px rgba(103, 194, 58, 0.3);
  transform: translateY(-1px);
}

.view-logs-btn:active {
  background: #5daf34;
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.2);
}

.view-logs-btn .icon {
  font-size: 14px;
  line-height: 1;
}

.view-logs-btn span {
  font-size: 14px;
}

/* 自定义主要操作按钮样式 */
.custom-upload-btn {
  background: #409EFF;
  color: white;
  border: none;
  border-radius: 8px;
  width: 120px;
  height: 44px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.custom-upload-btn:hover {
  background: #66b1ff;
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
  transform: translateY(-1px);
}

.custom-upload-btn:active {
  background: #3a8ee6;
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.custom-upload-btn i {
  font-size: 14px;
  margin-right: 6px;
}

.custom-upload-btn span {
  font-size: 14px;
}

/* 用户列表容器 */
.user-list-container {
  flex: 1;
  min-height: 0;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-radius: 0;
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner p {
  margin: 0;
  color: #86868b;
  font-size: 16px;
}

/* 表格样式 */
.table-container {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
}

.table-header {
  display: grid;
  --actions-col-width: 140px;
  grid-template-columns: 80px 1fr 1fr 108px 1fr 1fr 1fr var(--actions-col-width);
  --table-pad-x: 20px;
  box-sizing: border-box;
  width: 100%;
  min-width: 980px;
  background: #f8f9fa;
  padding: 16px var(--table-pad-x);
  font-weight: 600;
  color: #1d1d1f;
  font-size: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 4;
}

.content-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-row {
  display: grid;
  --actions-col-width: 140px;
  grid-template-columns: 80px 1fr 1fr 108px 1fr 1fr 1fr var(--actions-col-width);
  --table-pad-x: 20px;
  box-sizing: border-box;
  width: 100%;
  min-width: 980px;
  padding: 16px var(--table-pad-x);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  align-items: center;
  transition: all 0.2s ease;
  gap: 16px;
}

.table-row:hover {
  background: rgba(0, 0, 0, 0.02);
}

.header-cell, .cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell.actions-col {
  overflow: visible !important;
}

/* 固定列：left/right 与横向 padding 一致 */
.cell.id-col, .header-cell.id-col {
  font-weight: 600;
  color: #666;
  position: sticky;
  left: var(--table-pad-x, 20px);
  z-index: 3;
  box-sizing: border-box;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 3px 0 6px -4px rgba(0, 0, 0, 0.12);
  min-width: 0;
  max-width: none;
}

.header-cell.id-col {
  z-index: 5;
  background: #f8f9fa;
}

.cell.id-col {
  background: #fff;
}

.table-row:hover .cell.id-col {
  background: #fafafa;
}

.cell.actions-col, .header-cell.actions-col {
  position: sticky;
  right: var(--table-pad-x, 20px);
  z-index: 3;
  box-sizing: border-box;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: -3px 0 6px -4px rgba(0, 0, 0, 0.12);
  width: var(--actions-col-width, 140px);
  min-width: var(--actions-col-width, 140px);
  max-width: var(--actions-col-width, 140px);
}

.cell.admin-col, .header-cell.admin-col {
  min-width: 72px;
}

.header-cell.actions-col {
  z-index: 5;
  background: #f8f9fa;
}

.cell.actions-col {
  background: #fff;
}

.table-row:hover .cell.actions-col {
  background: #fafafa;
}

.sort-cell {
  cursor: pointer;
  user-select: none;
  gap: 6px;
}

.sort-icon {
  margin-left: 6px;
  font-size: 12px;
  color: #86868b;
}

.cell {
  color: #1d1d1f;
}

/* 用户名列特殊处理 */
.cell.username-col {
  min-width: 120px;
  font-weight: 500;
  flex: 1;
}

/* 改善滚动条样式 */
.table-scroll::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.table-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 在小屏幕上确保用户名不被挤压 */
@media (max-width: 1024px) {
  .cell.username-col {
    min-width: 100px;
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .cell.username-col {
    min-width: 120px;
    font-size: 13px;
  }
}

.admin-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.admin-badge.admin {
  background: rgba(52, 199, 89, 0.1);
  color: #34c759;
}

.admin-badge.agent {
  background: rgba(255, 149, 0, 0.1);
  color: #ff9500;
}

.admin-badge.config-admin {
  background: rgba(88, 86, 214, 0.1);
  color: #5856d6;
}

.admin-badge.normal {
  background: rgba(0, 0, 0, 0.05);
  color: #86868b;
}

.expiry-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.expiry-badge.normal {
  background: rgba(52, 199, 89, 0.1);
  color: #34c759;
}

.expiry-badge.soon {
  background: rgba(255, 149, 0, 0.1);
  color: #ff9500;
}

.expiry-badge.warning {
  background: rgba(255, 59, 48, 0.1);
  color: #ff3b30;
}

.expiry-badge.expired {
  background: rgba(255, 59, 48, 0.2);
  color: #ff3b30;
  font-weight: 600;
}

.expiry-badge.deleted {
  background: rgba(0, 0, 0, 0.15);
  color: #666;
  font-weight: 600;
  text-decoration: line-through;
}

.expiry-badge.unknown {
  background: rgba(0, 0, 0, 0.05);
  color: #86868b;
}

.expiry-badge.admin-no-expiry {
  background: rgba(128, 90, 213, 0.1);
  color: #805ad5;
  font-weight: 600;
}

.user-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
}

.action-btn--menu {
  color: #007aff;
  border-color: rgba(0, 122, 255, 0.3);
  min-width: 72px;
}

.action-menu-caret {
  font-size: 10px;
  margin-left: 2px;
  opacity: 0.7;
}

.action-btn {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.action-btn:active:not(:disabled) {
  transform: translateY(0);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn.primary {
  color: #fff;
  background: #007aff;
  border-color: #007aff;
}

.action-btn.primary:hover:not(:disabled) {
  background: #0066d6;
  border-color: #0066d6;
}

.action-btn.warning {
  color: #ff9500;
  border-color: rgba(255, 149, 0, 0.3);
}

.action-btn.warning:hover:not(:disabled) {
  background: rgba(255, 149, 0, 0.1);
  border-color: rgba(255, 149, 0, 0.5);
}

.action-btn.danger {
  color: #ff3b30;
  border-color: rgba(255, 59, 48, 0.3);
}

.action-btn.danger:hover:not(:disabled) {
  background: rgba(255, 59, 48, 0.1);
  border-color: rgba(255, 59, 48, 0.5);
}

.action-btn.edit {
  color: #007aff;
  border-color: rgba(0, 122, 255, 0.2);
}

.action-btn.edit:hover {
  background: rgba(0, 122, 255, 0.1);
}

.action-btn.reset {
  color: #ff9500;
  border-color: rgba(255, 149, 0, 0.2);
}

.action-btn.reset:hover {
  background: rgba(255, 149, 0, 0.1);
}

.action-btn.expire {
  color: #ff9500;
  border-color: rgba(255, 149, 0, 0.2);
}

.action-btn.expire:hover:not(:disabled) {
  background: rgba(255, 149, 0, 0.1);
}

.action-btn.delete {
  color: #ff3b30;
  border-color: rgba(255, 59, 48, 0.2);
}

.action-btn.delete:hover:not(:disabled) {
  background: rgba(255, 59, 48, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #86868b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #86868b;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px 0 32px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 28px;
}

.modal-header h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1d1d1f;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #86868b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #1d1d1f;
}

.modal-body {
  padding: 0 32px;
  max-height: 65vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 500;
  color: #1d1d1f;
}

.form-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.form-label-row label {
  margin-bottom: 0;
}

.btn-generate {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #007aff;
  background: rgba(0, 122, 255, 0.1);
  border: 1px solid rgba(0, 122, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-generate:hover:not(:disabled) {
  background: rgba(0, 122, 255, 0.15);
  border-color: rgba(0, 122, 255, 0.3);
  transform: translateY(-1px);
}

.btn-generate:active:not(:disabled) {
  transform: translateY(0);
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-input {
  width: 100%;
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s ease;
  background: white;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.form-input:disabled {
  background: #f5f5f7;
  color: #86868b;
}

.form-textarea {
  width: 100%;
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s ease;
  background: white;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  line-height: 1.5;
}

.form-textarea:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.form-textarea:disabled {
  background: #f5f5f7;
  color: #86868b;
}

/* 开关样式 */
.switch-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.switch-container {
  position: relative;
}

.switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 30px;
  background: #e5e5ea;
  border-radius: 15px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.switch-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 26px;
  height: 26px;
  background: white;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.switch-input:checked + .switch-slider {
  background: #34c759;
}

.switch-input:checked + .switch-slider::before {
  transform: translateX(20px);
}

.switch-input:disabled + .switch-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-help-text {
  font-size: 12px;
  color: #86868b;
  margin-top: 8px;
}

.reset-password-info {
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(0, 122, 255, 0.05);
  border: 1px solid rgba(0, 122, 255, 0.1);
  border-radius: 8px;
}

.reset-password-info p {
  margin: 0;
  color: #007aff;
  font-size: 14px;
}


.user-info {
  margin-bottom: 24px;
  padding: 20px;
  background: rgba(0, 122, 255, 0.05);
  border: 1px solid rgba(0, 122, 255, 0.1);
  border-radius: 10px;
}

.user-info p {
  margin: 10px 0;
  color: #1d1d1f;
  font-size: 15px;
  line-height: 1.6;
}

.operation-description {
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 149, 0, 0.05);
  border: 1px solid rgba(255, 149, 0, 0.1);
  border-radius: 10px;
}

.description-title {
  font-weight: 600;
  color: #ff9500;
  font-size: 14px;
  margin-bottom: 10px;
}

.description-content {
  color: #1d1d1f;
  font-size: 14px;
  line-height: 1.6;
}

/* 自定义下拉框样式 */
.custom-select {
  position: relative;
  width: 160px;
  flex-shrink: 0;
}

/* 只针对游戏选择下拉框增加宽度 */
.filter-container > .custom-select:first-child {
  width: 200px;
}

/* 对话框内的下拉框宽度为100% */
.modal-body .custom-select,
.form-group .custom-select {
  width: 100%;
}

.custom-select.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.select-trigger {
  width: 100%;
  padding: 0 40px 0 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  height: 44px;
  box-sizing: border-box;
}

.select-trigger:hover {
  border-color: #007aff;
}

.select-trigger.active {
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.select-value {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: #1d1d1f;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-arrow {
  font-size: 12px;
  color: #86868b;
  transition: transform 0.2s ease;
  position: absolute;
  right: 16px;
}

.select-arrow.rotated {
  transform: rotate(180deg);
}

.custom-select.is-open {
  z-index: 1001;
}

.filter-container > .custom-select.parent-agent-filter-select {
  width: 180px;
}

.select-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  max-height: 280px;
  overflow-x: hidden;
  overflow-y: auto;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.select-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover {
  background: rgba(0, 122, 255, 0.05);
}

.select-option.selected {
  background: rgba(0, 122, 255, 0.1);
  color: #007aff;
}

.option-text {
  font-size: 16px;
  font-weight: 500;
  color: inherit;
}

.option-desc {
  font-size: 13px;
  color: #86868b;
  line-height: 1.3;
}

.select-option.selected .option-desc {
  color: rgba(0, 122, 255, 0.7);
}

.modal-footer {
  padding: 28px 32px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn.secondary {
  background: #f5f5f7;
  color: #1d1d1f;
}

.btn.secondary:hover {
  background: #e5e5ea;
}

.btn.primary {
  background: #007aff;
  color: white;
}

.btn.primary:hover {
  background: #0056cc;
}

.btn.warning {
  background: #ff9500;
  color: white;
}

.btn.warning:hover {
  background: #cc7700;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* 桌面端表格布局 */
.desktop-table {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mobile-cards {
  display: none;
  overflow: visible;
  flex: 1;
}

/* 分页控件样式 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  padding: 20px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.pagination-info {
  font-size: 14px;
  color: #86868b;
  font-weight: 500;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pagination-page-size {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #86868b;
  font-weight: 500;
}

.page-size-label {
  white-space: nowrap;
}

.pagination-page-size :deep(.page-size-select) {
  width: 108px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: white;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #007aff;
  color: white;
  border-color: #007aff;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  gap: 6px;
}

.pagination-page {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: white;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-page:hover:not(.active) {
  background: rgba(0, 122, 255, 0.1);
  border-color: #007aff;
  color: #007aff;
}

.pagination-page.active {
  background: #007aff;
  color: white;
  border-color: #007aff;
}

/* 移动端卡片布局 */
.user-card {
  background: white;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.user-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.user-basic-info {
  flex: 1;
}

.username {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 3px 0;
}

.user-id {
  font-size: 11px;
  color: #86868b;
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 6px;
  border-radius: 10px;
}

.card-body {
  margin-bottom: 10px;
}

/* 窗口信息行 - 三个数据在同一行 */
.window-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.window-stat {
  display: flex;
  align-items: center;
  gap: 2px;
}

.stat-label {
  font-size: 10px;
  color: #86868b;
  font-weight: 500;
}

.stat-value {
  font-size: 12px;
  color: #1d1d1f;
  font-weight: 600;
}

/* 游戏信息行 */
.game-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.parent-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.info-label {
  font-size: 11px;
  color: #86868b;
  font-weight: 500;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: #1d1d1f;
  font-weight: 500;
}

/* 到期信息行 */
.expiry-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.card-actions {
  display: flex;
  gap: 4px;
  justify-content: space-between;
  flex-wrap: wrap;
  min-height: 32px;
  align-items: stretch;
}

.card-actions .action-btn {
  flex: 1;
  min-width: 0;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 2px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
}

.card-actions .action-btn .icon {
  font-size: 11px;
}

.card-actions .action-btn .btn-text {
  font-size: 10px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .table-header,
  .table-row {
    --actions-col-width: 140px;
    grid-template-columns: 70px 1fr 1fr 100px 1fr 1fr 1fr var(--actions-col-width);
    gap: 12px;
    min-width: 1200px;
  }
}

@media (max-width: 1024px) {
  .table-header,
  .table-row {
    --actions-col-width: 140px;
    grid-template-columns: 60px 1fr 1fr 96px 1fr 1fr 1fr var(--actions-col-width);
    gap: 8px;
    --table-pad-x: 16px;
    padding: 12px var(--table-pad-x);
    min-width: 1160px;
  }
}

@media (max-width: 768px) {
  /* 移动端滚动优化：由 layout 的 route-view-scroll 承载滚动 */
  .page-content {
    height: auto;
    min-height: 0;
    flex: none;
    display: block;
    overflow: visible;
  }

  .user-mgmt-toolbar-card {
    margin-bottom: 8px;
  }

  .mobile-search-row {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  .mobile-search-row .search-container {
    flex: 1;
    min-width: 0;
  }

  .mobile-filter-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-shrink: 0;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    background: #fff;
    color: #606266;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
  }

  .mobile-filter-badge {
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: #409eff;
    color: #fff;
    font-size: 11px;
    line-height: 16px;
    text-align: center;
  }

  .mobile-filter-toggle-icon {
    font-size: 10px;
    color: #909399;
  }

  .filter-container.is-collapsed {
    display: none;
  }

  /* 在移动端允许表格水平滚动，而不是完全隐藏 */
  .table-header,
  .table-row {
    --actions-col-width: 140px;
    grid-template-columns: 50px 1fr 1fr 90px 1fr 1fr 1fr var(--actions-col-width);
    gap: 4px;
    --table-pad-x: 12px;
    padding: 8px var(--table-pad-x);
    min-width: 1000px;
  }

  .cell {
    font-size: 12px;
  }

  .cell.username-col {
    min-width: 120px;
    font-size: 13px;
  }

  .user-management {
    flex: none;
    width: 100%;
    min-height: 0;
    overflow: visible;
  }

  .user-mgmt-body-card {
    flex: none;
    overflow: visible;
  }

  .user-mgmt-body-card :deep(.el-card__body) {
    overflow: visible !important;
    flex: none;
    min-height: 0;
  }

  .content-container {
    overflow: visible;
    flex: none;
    min-height: 0;
  }
  
  .user-list-container {
    overflow: visible;
    flex: none;
  }
  
  /* 隐藏桌面端表格，显示移动端卡片 */
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: block;
    overflow: visible;
    padding-bottom: 32px;
  }
  
  .page-header {
    margin-bottom: 0;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 10;
    padding: 8px 10px;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
  
  .search-section {
    max-width: none;
    flex-direction: column;
    gap: 6px;
    align-items: stretch;
  }
  
  .search-container {
    min-width: auto;
  }
  
  .filter-container {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    align-items: stretch;
  }

  .custom-select {
    width: 100%;
    min-width: 0;
  }

  .filter-container > .custom-select:first-child,
  .filter-container > .custom-select.parent-agent-filter-select {
    width: 100%;
  }

  .header-actions {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    width: 100%;
  }

  .reset-filter-btn,
  .view-logs-btn,
  .custom-upload-btn {
    flex: 1;
    min-width: 0;
    height: 32px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: 6px;
  }

  .reset-filter-btn {
    width: auto;
  }

  .view-logs-btn span:not(.icon) {
    display: none;
  }

  .view-logs-btn .icon {
    margin: 0;
  }

  .custom-upload-btn {
    width: auto;
    justify-content: center;
  }

  .custom-upload-btn i {
    margin-right: 4px;
  }
  
  .search-input {
    height: 32px;
    padding: 6px 32px 6px 10px;
    font-size: 14px;
    border-radius: 6px;
  }

  .select-trigger {
    min-height: 32px;
    height: 32px;
    padding: 0 28px 0 10px;
    border-radius: 6px;
  }

  .select-value {
    font-size: 12px;
    line-height: 16px;
  }

  .select-arrow {
    right: 10px;
    font-size: 10px;
  }

  .select-option {
    padding: 8px 10px;
  }
  
  .modal-container {
    width: 95%;
    margin: 20px;
  }
  
  /* 移动端卡片样式调整 */
  .user-card {
    margin-bottom: 6px;
    padding: 8px;
  }
  
  .username {
    font-size: 15px;
  }
  
  .card-body {
    margin-bottom: 8px;
  }
  
  /* 操作按钮保持横向排列 */
  .card-actions {
    gap: 3px;
  }
  
  .card-actions {
    gap: 3px;
    flex-wrap: wrap;
  }
  
  .card-actions .action-btn {
    padding: 6px 1px;
    font-size: 9px;
    flex: 1 1 48%;
    max-width: 48%;
    margin-bottom: 2px;
  }
  
  .card-actions .action-btn .btn-text {
    font-size: 9px;
  }
  
  .card-actions .action-btn .icon {
    font-size: 10px;
  }
  
  /* 移动端分页样式 */
  .pagination-container {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    align-items: stretch;
  }

  .pagination-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
  }
  
  .pagination-info {
    font-size: 13px;
  }

  .pagination-page-size {
    width: 100%;
    justify-content: space-between;
  }

  .pagination-page-size :deep(.page-size-select) {
    width: 120px;
  }
  
  .pagination-controls {
    width: 100%;
    justify-content: center;
  }
  
  .pagination-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .pagination-page {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .page-content {
    height: auto;
    min-height: 100%;
    overflow: visible;
    -webkit-overflow-scrolling: touch;
  }
  
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 10;
    padding: 6px 8px;
  }

  .filter-container {
    gap: 4px;
  }

  .header-actions {
    gap: 4px;
  }

  .reset-filter-btn,
  .view-logs-btn,
  .custom-upload-btn {
    height: 30px;
    font-size: 11px;
    padding: 0 6px;
  }

  .custom-upload-btn span {
    font-size: 11px;
  }
  
  .user-card {
    padding: 6px;
    margin-bottom: 4px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 6px;
    padding-bottom: 4px;
  }
  
  .username {
    font-size: 14px;
  }
  
  .user-id {
    font-size: 9px;
  }
  
  .card-body {
    margin-bottom: 6px;
  }
  
  /* 保持按钮横向排列，进一步紧凑 */
  .card-actions .action-btn {
    padding: 4px 1px;
    gap: 1px;
  }
  
  .card-actions .action-btn .btn-text {
    font-size: 8px;
  }
  
  .card-actions .action-btn .icon {
    font-size: 9px;
  }

  /* 超小屏幕按钮分两行显示 */
  .card-actions {
    gap: 2px;
    flex-wrap: wrap;
  }
  
  .card-actions .action-btn {
    flex: 1 1 48%;
    max-width: 48%;
    margin-bottom: 2px;
  }
  
  .modal-container {
    width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
    overflow-y: auto;
  }
  
  /* 确保卡片区域有足够的底部空间 */
  .mobile-cards {
    padding-bottom: 30px;
  }
  
  /* 小屏幕分页样式 */
  .pagination-container {
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    align-items: stretch;
  }

  .pagination-left {
    gap: 8px;
  }
  
  .pagination-info {
    font-size: 12px;
  }
  
  .pagination-controls {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .pagination-btn {
    padding: 5px 10px;
    font-size: 12px;
  }
  
  .pagination-pages {
    gap: 4px;
  }
  
  .pagination-page {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }
  
  /* 小屏幕下窗口信息行的调整 */
  .window-info-row {
    padding: 3px 6px;
    margin-bottom: 4px;
  }
  
  .stat-label {
    font-size: 9px;
  }
  
  .stat-value {
    font-size: 11px;
  }
  
  .game-info {
    margin-bottom: 4px;
  }

  .parent-info {
    margin-bottom: 4px;
  }
  
  .info-label {
    font-size: 10px;
  }
  
  .info-value {
    font-size: 11px;
  }
  
  .expiry-info {
    gap: 4px;
    padding: 3px 6px;
  }
}

.windows-col {
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  white-space: nowrap;
}

.window-stat--combined .stat-value {
  font-variant-numeric: tabular-nums;
}
</style>
